 <template lang="pug">
.page.is-form.with-cover.page__light.login.pd-page
  .page__form.inner-gap(v-loading="submitting")
    el-form.form(
      ref="form"
      label-position="top"
      autocomplete="on"
      name="login"
      :model="form"
      :rules="rules"
      @submit.native="submit"
      @keyup.native.enter="submit")

      .pd-container.framed
        h1.page__title Login
        .page__description
          span.mr-2 Do not have an account?
          nuxt-link.linked(:to="{ name: 'register' }" title="Register" v-click="() => $amplitude.track('CLICK_REQUEST_ACCESS')") Request access →

        client-only
          el-form-item.boxed(
            label="Email"
            prop="email")
            el-input(v-model="form.email" name="email" autocomplete="on")

          el-form-item.boxed(label="Password" prop="password")
            el-input(type="password" v-model="form.password" name="password" autocomplete="on" show-password)

          el-form-item(prop="user_role")
            el-radio-group(v-model="form.user_role" name="role" autocomplete="on")
              el-radio-button(label="vendor") Vendor
              el-radio-button(label="buyer") Buyer
              el-radio-button(label="logistics_partner")
                span.px-2 LP
              el-radio-button(label="broker") Broker

        RecaptchaNotice

        el-form-item
          el-button(type="primary" @click="submit" size="large") Login

        .page__description
          span.mr-2 Unable to login?
          nuxt-link.linked(
            :to="{ name: 'reset-password' }"
            title="Reset your password"
          ) Reset your password →

</template>

<script>
import { mapGetters } from 'vuex'
import reCaptcha from '~/mixins/reCaptcha'
import RecaptchaNotice from '~/components/recaptcha/RecaptchaNotice'

export default {
  name: 'Login',

  components: {
    RecaptchaNotice
  },

  mixins: [reCaptcha],

  layout: 'landing',

  data () {
    return {
      submitting: false,
      form: {
        email: '',
        password: '',
        user_role: null
      },
      rules: {
        email: [
          { required: true, message: 'Please input your email' },
          { type: 'email', message: 'Please input correct email address' }
        ],
        password: [
          { max: 256, message: 'Maximum is 256 characters' },
          { required: true, message: 'Please input your password' },
          { min: 8, message: 'Password must have 8 characters minimum' }
        ],
        user_role: { required: true, message: 'Please select your role' }
      }
    }
  },

  fetch ({ redirect, store, query }) {
    const logged = store.getters['authentication/logged']

    if (!logged) return

    if (query.to) return redirect(query.to)

    return redirect(store.getters['authentication/redirectTo'])
  },

  head () {
    return {
      title: 'Login',
      meta: [{
        hid: 'description',
        name: 'description',
        content: 'Unlike other food distribution companies, the Pod platform uses a full-service, transparent, end-to-end distributor model.'
      }]
    }
  },

  computed: {
    ...mapGetters({
      logged: 'authentication/logged',
      redirectTo: 'authentication/redirectTo',
      user: 'authentication/user',
      isVendor: 'authentication/isVendor',
      addedBothBankAndCard: 'authentication/addedBothBankAndCard'
    }),

    to () {
      let to = this.$route.query.to

      if (this.isVendor &&
        !this.addedBothBankAndCard &&
        !this.$preferences.get('$vendorFirstLogin')
      ) {
        to = 'vendors/settings/payments'
        this.$preferences.set('$vendorFirstLogin', true)
      }

      try {
        const { route } = this.$router.resolve(to)

        return route
      } catch (e) {
        return this.redirectTo
      }
    }
  },

  mounted () {
    const { get } = this.$preferences

    this.form = {
      email: this.$route.query?.email || get('$lastUserEmail', ''),
      user_role: this.$route.query?.role || get('$lastUserRole', null)
    }

    if (this.to?.query?.vendor_company_id) {
      this.$preferences.set('lastVendorCompany', this.to.query.vendor_company_id)
    }

    this.$nextTick(() => {
      this.$refs.form.clearValidate()
    })
  },

  methods: {
    redirect () {
      const to = this.$route.query.to

      if (!to)
        return this.$router.replace(this.redirectTo)

      const { route } = this.$router.resolve(to)

      route.matched?.length
        ? this.$router.replace(to)
        : window.location.href = to
    },

    submit () {
      if (this.submitting) return

      this.$refs.form.validate(valid => {
        if (!valid) return false

        return this.$recaptcha
          .execute('login')
          .then(token => {
            this.submitting = true

            return this.$store
              .dispatch('authentication/login', { ...this.form, token })
              .then(this.redirect)
              .finally(() => {
                this.submitting = false
              })
          })
          .catch(this.$debug.log)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.login {
  .linked {
    display: inline-block;
  }
}
</style>
